import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';
import { updateDriverType } from './api';

const QUOTE_STYLES = ['DOUBLE_QUOTE', 'BRACKET', 'BACKTICK'];

const ConfigureDriverModal = ({ show, onHide, driver, onSuccess }) => {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !driver) return;
    setError(null);
    setForm({
      displayName: driver.displayName || '',
      jdbcUrlTemplate: driver.jdbcUrlTemplate || '',
      defaultPort: driver.defaultPort != null ? driver.defaultPort : 0,
      defaultSchema: driver.defaultSchema || '',
      identifierQuoteStyle: driver.identifierQuoteStyle || 'DOUBLE_QUOTE',
    });
  }, [show, driver]);

  if (!driver) return null;

  const handleSave = async () => {
    setError(null);
    if (form.jdbcUrlTemplate
        && (!form.jdbcUrlTemplate.includes('{host}') || !form.jdbcUrlTemplate.includes('{database}'))) {
      setError('JDBC URL template must contain {host} and {database}.');
      return;
    }
    setSubmitting(true);
    try {
      await updateDriverType(driver.typeName || driver.driverId, form);
      if (onSuccess) onSuccess();
      onHide();
    } catch (e) {
      setError(e?.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaCog className="mr-2" />
          Configure {driver.displayName || driver.typeName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {driver.jarFileName && (
          <Form.Group className="mb-2">
            <Form.Label>Jar file</Form.Label>
            <Form.Control type="text" value={driver.jarFileName} readOnly plaintext />
          </Form.Group>
        )}
        <Form.Group className="mb-2">
          <Form.Label>Display name</Form.Label>
          <Form.Control
            type="text"
            data-testid="configure-driver-display-name"
            value={form.displayName || ''}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>JDBC URL template</Form.Label>
          <Form.Control
            type="text"
            placeholder="jdbc:foo://{host}:{port}/{database}"
            value={form.jdbcUrlTemplate || ''}
            onChange={(e) => setForm({ ...form, jdbcUrlTemplate: e.target.value })}
          />
          <Form.Text className="text-muted">Must contain {'{host}'} and {'{database}'}.</Form.Text>
        </Form.Group>
        <Row>
          <Col xs={6}>
            <Form.Group className="mb-2">
              <Form.Label>Default port</Form.Label>
              <Form.Control
                type="number"
                min={0}
                max={65535}
                value={form.defaultPort != null ? form.defaultPort : 0}
                onChange={(e) => setForm({ ...form, defaultPort: Number(e.target.value) })}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group className="mb-2">
              <Form.Label>Default schema</Form.Label>
              <Form.Control
                type="text"
                value={form.defaultSchema || ''}
                onChange={(e) => setForm({ ...form, defaultSchema: e.target.value })}
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-2">
          <Form.Label>Identifier quote style</Form.Label>
          <Form.Control
            as="select"
            value={form.identifierQuoteStyle || 'DOUBLE_QUOTE'}
            onChange={(e) => setForm({ ...form, identifierQuoteStyle: e.target.value })}
          >
            {QUOTE_STYLES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
        {error && <Alert variant="danger">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={submitting}>
          {submitting && <Spinner animation="border" size="sm" className="mr-2" />}
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfigureDriverModal;
